// src/pages/tickets/components/TicketComentarios.tsx
import { useState } from 'react';
import { Card, Avatar, Button, Textarea, Spinner } from '@/components/ui';
import { Send, Lock, Trash2 } from 'lucide-react';
import { formatDateTime, cn } from '@/lib/utils';
import { useComentarios, useCrearComentario, useEliminarComentario } from '@/hooks/queries/useTickets';
import { useAuthStore } from '@/stores/authStore';

interface Props {
  ticketId: string;
}

export function TicketComentarios({ ticketId }: Props) {
  const { data: comentarios, isLoading } = useComentarios(ticketId);
  const crearMutation = useCrearComentario();
  const eliminarMutation = useEliminarComentario();
  const { user } = useAuthStore();

  const [contenido, setContenido] = useState('');
  const [esInterno, setEsInterno] = useState(false);

  const handleEnviar = async () => {
    if (!contenido.trim()) return;

    await crearMutation.mutateAsync({
      ticketId,
      data: { contenido, es_interno: esInterno },
    });
    setContenido('');
  };

  const handleEliminar = async (comentarioId: string) => {
    if (confirm('Eliminar este comentario?')) {
      await eliminarMutation.mutateAsync({ ticketId, comentarioId });
    }
  };

  if (isLoading) {
    return (
      <Card className="p-4 flex justify-center">
        <Spinner />
      </Card>
    );
  }

  return (
    <Card className="p-4">
      {/* Lista de comentarios */}
      <div className="space-y-4 mb-6">
        {comentarios?.map((comentario) => (
          <div
            key={comentario.id}
            className={cn(
              'flex gap-3 p-3 rounded-lg',
              comentario.es_interno && 'bg-orange-50 border border-orange-200'
            )}
          >
            <Avatar fallback={comentario.autor?.nombre} size="sm" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm">{comentario.autor?.nombre}</span>
                <span className="text-xs text-gray-400">
                  {formatDateTime(comentario.created_at)}
                </span>
                {comentario.es_interno && (
                  <span className="flex items-center gap-1 text-xs text-orange-600">
                    <Lock className="h-3 w-3" />
                    Interno
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{comentario.contenido}</p>
            </div>
            {comentario.autor_id === user?.id && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleEliminar(comentario.id)}
                className="text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}

        {comentarios?.length === 0 && (
          <p className="text-center text-gray-500 py-4">No hay comentarios aun</p>
        )}
      </div>

      {/* Formulario de nuevo comentario */}
      <div className="border-t pt-4">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => setEsInterno(!esInterno)}
            className={cn(
              'flex items-center gap-1 text-xs px-2 py-1 rounded',
              esInterno ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
            )}
          >
            <Lock className="h-3 w-3" />
            {esInterno ? 'Nota Interna' : 'Comentario Publico'}
          </button>
        </div>
        <div className="flex gap-2">
          <Textarea
            value={contenido}
            onChange={(e) => setContenido(e.target.value)}
            placeholder={esInterno ? 'Escriba una nota interna...' : 'Escriba un comentario...'}
            className={cn('flex-1', esInterno && 'border-orange-300')}
            rows={2}
          />
          <Button
            onClick={handleEnviar}
            disabled={!contenido.trim() || crearMutation.isPending}
            loading={crearMutation.isPending}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
